
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import SimpleRichTextEditor from "@/components/blog/SimpleRichTextEditor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CreateBlog = () => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [category, setCategory] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [textDirection, setTextDirection] = useState<"ltr" | "rtl">("ltr");
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Auto-generate excerpt from content
  useEffect(() => {
    if (content) {
      // Create a temporary div to strip HTML tags
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      const textContent = tempDiv.textContent || '';
      // Get first 150 characters
      const newExcerpt = textContent.substring(0, 150) + (textContent.length > 150 ? '...' : '');
      setExcerpt(newExcerpt);
    }
  }, [content]);
  
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
      // Create a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      
      // Upload image immediately
      await handleImageUpload(file);
    }
  };
  
  const handleImageUpload = async (file: File) => {
    if (!file) return;
    
    setIsImageUploading(true);
    
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;
      
      const { error: uploadError, data } = await supabase.storage
        .from('blog-images')
        .upload(filePath, file);
        
      if (uploadError) {
        throw uploadError;
      }
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('blog-images')
        .getPublicUrl(filePath);
        
      setImageUrl(publicUrl);
      
      toast({
        title: "Image uploaded",
        description: "Your image has been uploaded successfully.",
      });
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsImageUploading(false);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Authentication required",
        description: "You must be logged in to create a blog post",
        variant: "destructive",
      });
      return;
    }
    
    if (!title || !content || !category) {
      toast({
        title: "Missing fields",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .insert({
          title,
          content,
          excerpt,
          category,
          image_url: imageUrl,
          user_id: user.id,
          published: true,
          text_direction: textDirection, // Add text_direction field
        })
        .select()
        .single();
        
      if (error) throw error;
      
      toast({
        title: "Blog post created",
        description: "Your blog post has been published successfully!",
      });
      
      // Navigate to the blog post
      navigate(`/blog/${data.id}`);
    } catch (error: any) {
      console.error('Error creating blog post:', error);
      toast({
        title: "Failed to create blog post",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Layout title="Create Blog Post" description="Create a new blog post with our rich text editor">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="text-3xl font-bold mb-8">Create Blog Post</h1>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter blog post title"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Input
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Enter category (e.g., Technology, Design, etc.)"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label>Text Direction</Label>
            <Select value={textDirection} onValueChange={(value: "ltr" | "rtl") => setTextDirection(value)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select text direction" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ltr">Left to Right (LTR)</SelectItem>
                <SelectItem value="rtl">Right to Left (RTL)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-gray-500">Choose RTL for Arabic, Hebrew, or other right-to-left languages</p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="image">Featured Image</Label>
            <div className="flex items-center gap-4">
              <Input
                id="image"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="max-w-xs"
              />
              {isImageUploading && <span className="text-sm text-gray-500">Uploading...</span>}
            </div>
            
            {imagePreview && (
              <div className="mt-4">
                <p className="text-sm text-gray-500 mb-2">Image Preview:</p>
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full max-w-md h-auto rounded-lg object-cover"
                />
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="content">Content</Label>
            <Card className="min-h-[400px]">
              <CardContent className="p-0">
                <SimpleRichTextEditor 
                  value={content} 
                  onChange={setContent}
                  direction={textDirection}
                />
              </CardContent>
            </Card>
          </div>
          
          <div className="pt-4">
            <Button type="submit" disabled={isSubmitting || isImageUploading}>
              {isSubmitting ? "Publishing..." : "Publish Blog Post"}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CreateBlog;
